import { useState, useEffect, useRef } from 'react';
import {
  Dialog, DialogContent, DialogActions, Box, Stepper, Step, StepLabel,
  Typography, TextField, MenuItem, Grid, Button, Alert, CircularProgress,
  Paper, Avatar, Stack, Chip, IconButton, Divider, LinearProgress,
} from '@mui/material';
import {
  Person as PersonIcon, School as SchoolIcon, Group as ParentIcon,
  HealthAndSafety as HealthIcon, RateReview as ReviewIcon,
  CheckCircle as CheckIcon, NavigateBefore as BackIcon, NavigateNext as NextIcon,
  Save as SaveIcon, Close as CloseIcon,
  FamilyRestroom as FamilyIcon, Man as ManIcon, Woman as WomanIcon,
} from '@mui/icons-material';
import API from '../../BaseURL';
import swal from '../../utils/swal';
import PhotoCapture from '../../components/PhotoCapture';

const STEPS = [
  { label: 'Personal',  icon: <PersonIcon /> },
  { label: 'Academic',  icon: <SchoolIcon /> },
  { label: 'Parents',   icon: <FamilyIcon /> },
  { label: 'Guardian',  icon: <ParentIcon /> },
  { label: 'Health',    icon: <HealthIcon /> },
  { label: 'Review',    icon: <ReviewIcon /> },
];

const blankParent = {
  full_n: '', occu: '', emp_na: '', emp_add: '',
  mo_num: '', ho_tele_num: '', wo_tel_num: '',
  e_add: '', mail_add: '', highest: '',
};

const blank = {
  index_no: '', firstname: '', lastname: '', gender: 'Male', dob: '',
  rele: '', image: '',
  calss: '', type: 'Day', house: '', stay: false,
  re_dat: new Date().toISOString().slice(0, 10),
  parents: { father: { ...blankParent }, mother: { ...blankParent } },
  p_name: '', p_rele: '', p_occ: '', mobi: '', p_email: '', p_res: '',
  alle: '', me_hi: '', he_con: '', e_na: '', tele: '',
};

function SectionHeader({ icon, title, subtitle }) {
  return (
    <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
      <Avatar sx={{ bgcolor: 'primary.main', width: 44, height: 44 }}>{icon}</Avatar>
      <Box>
        <Typography variant="h6" fontWeight={800}>{title}</Typography>
        <Typography variant="body2" color="text.secondary">{subtitle}</Typography>
      </Box>
    </Stack>
  );
}

function ParentForm({ parent, onChange, accentColor = 'primary' }) {
  const set = (patch) => onChange({ ...parent, ...patch });
  return (
    <Grid container spacing={1.5}>
      <Grid item xs={12}>
        <TextField fullWidth size="small" label="Full Name"
          value={parent.full_n} onChange={(e) => set({ full_n: e.target.value })} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField fullWidth size="small" label="Occupation"
          value={parent.occu} onChange={(e) => set({ occu: e.target.value })} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField select fullWidth size="small" label="Highest Education"
          value={parent.highest} onChange={(e) => set({ highest: e.target.value })}>
          <MenuItem value="">—</MenuItem>
          <MenuItem value="No Education">No Education</MenuItem>
          <MenuItem value="Junior High">Junior High</MenuItem>
          <MenuItem value="Senior High">Senior High</MenuItem>
          <MenuItem value="Tertiary">Tertiary</MenuItem>
          <MenuItem value="Others">Others</MenuItem>
        </TextField>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField fullWidth size="small" label="Mobile Number"
          value={parent.mo_num} onChange={(e) => set({ mo_num: e.target.value })} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField fullWidth size="small" label="Email"
          value={parent.e_add} onChange={(e) => set({ e_add: e.target.value })} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField fullWidth size="small" label="Home Phone"
          value={parent.ho_tele_num} onChange={(e) => set({ ho_tele_num: e.target.value })} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField fullWidth size="small" label="Work Phone"
          value={parent.wo_tel_num} onChange={(e) => set({ wo_tel_num: e.target.value })} />
      </Grid>
      <Grid item xs={12}>
        <TextField fullWidth size="small" label="Employer / Workplace"
          value={parent.emp_na} onChange={(e) => set({ emp_na: e.target.value })} />
      </Grid>
      <Grid item xs={12}>
        <TextField fullWidth size="small" label="Mailing / Postal Address"
          value={parent.mail_add} onChange={(e) => set({ mail_add: e.target.value })} />
      </Grid>
    </Grid>
  );
}

export default function AdmissionWizard({ open, onClose, onSaved, editing = null }) {
  const [step, setStep]   = useState(0);
  const [form, setForm]   = useState(blank);
  const [classes, setCls] = useState([]);
  const [error, setErr]   = useState('');
  const [saving, setSav]  = useState(false);

  useEffect(() => {
    if (!open) return;
    setStep(0); setErr(''); setSav(false);
    if (editing) {
      setForm({
        ...blank, ...editing,
        stay: !!editing.stay,
        type: editing.type || 'Day',
        gender: editing.gender || 'Male',
        re_dat: editing.re_dat || blank.re_dat,
        // Use mobi if present, else fall back to legacy p_t_m
        mobi: editing.mobi || editing.p_t_m || '',
        // Normalize legacy free-text allergy values to '' so the Yes/No selector renders cleanly
        alle: editing.alle === 'Yes' || editing.alle === 'No' ? editing.alle : '',
        he_con: editing.he_con || '',
        me_hi:  editing.me_hi  || '',
        e_na:   editing.e_na   || '',
        tele:   editing.tele   || '',
        parents: {
          father: { ...blankParent, ...(editing.parents?.father || {}) },
          mother: { ...blankParent, ...(editing.parents?.mother || {}) },
        },
      });
    } else {
      setForm(blank);
    }
    API.get('/classes').then(r => setCls(r.data)).catch(() => {});
  }, [open, editing]);

  const [prefixPreview, setPrefixPreview] = useState('');
  useEffect(() => {
    if (open && !editing) {
      API.get('/admissions/next-index')
        .then(r => setPrefixPreview(r.data?.prefix || ''))
        .catch(() => setPrefixPreview(''));
    }
  }, [open, editing]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setParent = (which, val) => setForm((f) => ({ ...f, parents: { ...f.parents, [which]: val } }));

  const validate = () => {
    if (step === 0) {
      if (!form.firstname.trim()) return 'First name is required';
      if (!form.lastname.trim())  return 'Last name is required';
      if (!form.gender)           return 'Gender is required';
      if (!form.dob)              return 'Date of birth is required';
    }
    if (step === 1) {
      if (!form.calss)  return 'Class to enroll is required';
      if (!form.type)   return 'Status (Day or Boarder) is required';
      if (!form.re_dat) return 'Registration date is required';
      if (form.type === 'Boarder' && !form.house) return 'Boarding house is required for boarders';
    }
    if (step === 3) {
      if (!form.p_name.trim()) return 'Guardian full name is required';
      if (!form.p_rele.trim()) return 'Relationship to student is required';
      if (!form.mobi.trim())   return 'Guardian mobile number is required';
      if (!form.p_res.trim())  return 'Residential address is required';
      if (form.p_email && !/^\S+@\S+\.\S+$/.test(form.p_email)) return 'Invalid email address';
    }
    if (step === 4) {
      if (form.alle !== 'Yes' && form.alle !== 'No') return 'Please indicate whether the student has any allergies';
      if (form.alle === 'Yes') {
        if (!form.he_con.trim()) return 'Health conditions are required when allergies are present';
        if (!form.me_hi.trim())  return 'Medical history is required when allergies are present';
      }
      if (!form.e_na.trim()) return 'Emergency contact name is required';
      if (!form.tele.trim()) return 'Emergency contact phone is required';
    }
    return null;
  };

  const handleNext = () => {
    const err = validate();
    if (err) { setErr(err); return; }
    setErr('');
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };
  const handleBack = () => { setErr(''); setStep((s) => Math.max(s - 1, 0)); };

  const submit = async () => {
    if (!editing) {
      const ok = await swal.confirm({
        title: 'Register this student?',
        html: `<strong>${form.firstname} ${form.lastname}</strong> · ${form.gender}<br/>` +
              `Class: <strong>${form.calss}</strong> · ${form.type}<br/>` +
              `<small>An index number will be auto-generated.</small>`,
        confirmText: 'Yes, register',
        icon: 'question',
        tone: 'primary',
      });
      if (!ok) return;
    }
    setSav(true); setErr('');
    try {
      const payload = { ...form, stay: form.stay ? 1 : 0 };
      payload.p_t_m = payload.mobi || payload.p_t_m || '';
      if (editing) {
        await API.put(`/admissions/students/${editing.trans_code}`, payload);
        onSaved?.();
        onClose();
        swal.success(`${form.firstname} ${form.lastname} updated`, 'Saved');
      } else {
        const res = await API.post('/admissions', payload);
        onSaved?.();
        // Don't close — reset the wizard so the user can register the next
        // student straight away. Keep class, status and registration date so
        // back-to-back admissions for the same intake are quick.
        setForm({
          ...blank,
          calss:  form.calss,
          type:   form.type,
          stay:   form.stay,
          house:  form.house,
          re_dat: form.re_dat,
        });
        setStep(0);
        setErr('');
        swal.success(
          `Registered as ${res?.data?.index_no || 'new student'} — start the next admission or close.`,
          'Registration complete'
        );
      }
    } catch (e) {
      setErr(swal.pickError(e, 'Save failed'));
    } finally { setSav(false); }
  };

  const progress = ((step + 1) / STEPS.length) * 100;

  return (
    <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="md" fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
      <Box sx={{
        background: (t) => `linear-gradient(135deg, ${t.palette.primary.main} 0%, ${t.palette.secondary.main} 100%)`,
        color: '#fff', px: 3, pt: 2.5, pb: 2,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2,
      }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}>
            <SchoolIcon />
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight={800}>
              {editing ? 'Edit Student' : 'New Student Registration'}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.85 }}>
              Step {step + 1} of {STEPS.length} · {STEPS[step].label}
            </Typography>
          </Box>
        </Stack>
        <IconButton onClick={onClose} disabled={saving} sx={{ color: '#fff' }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <LinearProgress variant="determinate" value={progress}
        sx={{ height: 4, '& .MuiLinearProgress-bar': { bgcolor: 'primary.main' } }} />

      <Box sx={{ px: 3, pt: 2.5, bgcolor: 'background.default' }}>
        <Stepper activeStep={step} alternativeLabel>
          {STEPS.map((s, i) => (
            <Step key={s.label} completed={i < step}>
              <StepLabel>
                <Typography variant="caption" fontWeight={i === step ? 700 : 500}
                  color={i === step ? 'primary' : 'text.secondary'}>
                  {s.label}
                </Typography>
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      <DialogContent dividers sx={{ bgcolor: 'background.default', p: { xs: 2, sm: 3 } }}>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr('')}>{error}</Alert>}

        {/* STEP 0 — PERSONAL */}
        {step === 0 && (
          <Paper sx={{ p: 3 }}>
            <SectionHeader icon={<PersonIcon />} title="Personal Information"
              subtitle="The student's basic personal details" />
            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={4}>
                <PhotoCapture value={form.image} onChange={(img) => set({ image: img })} />
              </Grid>
              <Grid item xs={12} sm={8}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField fullWidth label="Index Number" disabled
                      value={editing
                        ? (form.index_no || '—')
                        : (prefixPreview ? `${prefixPreview}_<auto>` : 'Auto-generated on save')}
                      helperText={editing
                        ? 'Index numbers cannot be changed after registration'
                        : `Auto-generated as ${prefixPreview || 'PREFIX'}_<unique number> when you save · change the prefix in Settings`} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth required label="First Name"
                      value={form.firstname} onChange={(e) => set({ firstname: e.target.value })} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth required label="Last Name"
                      value={form.lastname} onChange={(e) => set({ lastname: e.target.value })} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField select fullWidth required label="Gender"
                      value={form.gender} onChange={(e) => set({ gender: e.target.value })}>
                      <MenuItem value="Male">Male</MenuItem>
                      <MenuItem value="Female">Female</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth required type="date" label="Date of Birth"
                      InputLabelProps={{ shrink: true }}
                      value={form.dob} onChange={(e) => set({ dob: e.target.value })} />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Paper>
        )}

        {/* STEP 1 — ACADEMIC */}
        {step === 1 && (
          <Paper sx={{ p: 3 }}>
            <SectionHeader icon={<SchoolIcon />} title="Academic Information"
              subtitle="Class, status and registration date" />
            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6}>
                <TextField select fullWidth required label="Class to Enroll"
                  value={form.calss} onChange={(e) => set({ calss: e.target.value })}>
                  <MenuItem value="">—</MenuItem>
                  {classes.map(c => <MenuItem key={c.trans_code} value={c.class}>{c.class}</MenuItem>)}
                  <MenuItem value="Alumna">Alumni (graduated)</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth required type="date" label="Registration Date"
                  InputLabelProps={{ shrink: true }}
                  value={form.re_dat} onChange={(e) => set({ re_dat: e.target.value })} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" fontWeight={700} color="text.secondary"
                  sx={{ textTransform: 'uppercase', letterSpacing: 0.6, display: 'block', mb: 1 }}>
                  Status *
                </Typography>
                <Stack direction="row" spacing={1}>
                  {['Day', 'Boarder'].map(opt => (
                    <Paper key={opt} variant="outlined"
                      onClick={() => set({ type: opt, stay: opt === 'Boarder' })}
                      sx={{
                        flex: 1, p: 1.5, textAlign: 'center', cursor: 'pointer',
                        borderColor: form.type === opt ? 'primary.main' : 'divider',
                        bgcolor: form.type === opt ? 'primary.main' : 'background.paper',
                        color: form.type === opt ? '#fff' : 'text.primary',
                        borderWidth: form.type === opt ? 2 : 1,
                        '&:hover': { borderColor: 'primary.main' },
                      }}>
                      <Typography variant="body2" fontWeight={700}>{opt} Student</Typography>
                    </Paper>
                  ))}
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Boarding House"
                  required={form.type === 'Boarder'}
                  disabled={form.type !== 'Boarder'}
                  value={form.house} onChange={(e) => set({ house: e.target.value })}
                  helperText={form.type !== 'Boarder' ? 'Only for boarders' : ' '} />
              </Grid>
            </Grid>
          </Paper>
        )}

        {/* STEP 2 — PARENTS (Father & Mother) */}
        {step === 2 && (
          <Paper sx={{ p: 3 }}>
            <SectionHeader icon={<FamilyIcon />} title="Parents' Information"
              subtitle="Father's and mother's details — both are optional but encouraged" />
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
                  <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}><ManIcon /></Avatar>
                  <Typography variant="overline" fontWeight={700} color="primary">Father</Typography>
                </Stack>
                <ParentForm parent={form.parents.father} onChange={(v) => setParent('father', v)} />
              </Grid>
              <Grid item xs={12} md={6}>
                <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
                  <Avatar sx={{ bgcolor: 'secondary.main', width: 32, height: 32 }}><WomanIcon /></Avatar>
                  <Typography variant="overline" fontWeight={700} color="secondary">Mother</Typography>
                </Stack>
                <ParentForm parent={form.parents.mother} onChange={(v) => setParent('mother', v)} />
              </Grid>
            </Grid>
          </Paper>
        )}

        {/* STEP 3 — GUARDIAN / PRIMARY CONTACT */}
        {step === 3 && (
          <Paper sx={{ p: 3 }}>
            <SectionHeader icon={<ParentIcon />} title="Guardian / Primary Contact"
              subtitle="The day-to-day contact responsible for the student" />
            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth required label="Full Name"
                  value={form.p_name} onChange={(e) => set({ p_name: e.target.value })} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField select fullWidth required label="Relationship to Student"
                  value={form.p_rele} onChange={(e) => set({ p_rele: e.target.value })}>
                  <MenuItem value="">—</MenuItem>
                  {['Father', 'Mother', 'Guardian', 'Uncle', 'Aunt', 'Grandparent', 'Sibling', 'Other'].map(r => (
                    <MenuItem key={r} value={r}>{r}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth required label="Mobile Phone"
                  value={form.mobi} onChange={(e) => set({ mobi: e.target.value })}
                  helperText="Primary phone number" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Occupation"
                  value={form.p_occ} onChange={(e) => set({ p_occ: e.target.value })} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth type="email" label="Email Address"
                  value={form.p_email} onChange={(e) => set({ p_email: e.target.value })} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth required multiline rows={2} label="Residential Address"
                  value={form.p_res} onChange={(e) => set({ p_res: e.target.value })} />
              </Grid>
            </Grid>
          </Paper>
        )}

        {/* STEP 4 — HEALTH */}
        {step === 4 && (
          <Paper sx={{ p: 3 }}>
            <SectionHeader icon={<HealthIcon />} title="Health & Emergency"
              subtitle="Allergy status, medical details and emergency contact" />

            <Typography variant="caption" fontWeight={700} color="text.secondary"
              sx={{ textTransform: 'uppercase', letterSpacing: 0.6, display: 'block', mb: 1 }}>
              Any Allergies? *
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mb: 2.5 }}>
              {['Yes', 'No'].map(opt => (
                <Paper key={opt} variant="outlined"
                  onClick={() => set(opt === 'No' ? { alle: 'No', he_con: '', me_hi: '' } : { alle: 'Yes' })}
                  sx={{
                    flex: 1, p: 1.5, textAlign: 'center', cursor: 'pointer',
                    borderColor: form.alle === opt ? 'primary.main' : 'divider',
                    bgcolor: form.alle === opt ? 'primary.main' : 'background.paper',
                    color: form.alle === opt ? '#fff' : 'text.primary',
                    borderWidth: form.alle === opt ? 2 : 1,
                    '&:hover': { borderColor: 'primary.main' },
                  }}>
                  <Typography variant="body2" fontWeight={700}>{opt}</Typography>
                </Paper>
              ))}
            </Stack>

            {form.alle === 'Yes' && (
              <Box sx={{ mb: 2.5 }}>
                <TextField fullWidth required label="Health Conditions" sx={{ mb: 2 }}
                  value={form.he_con} onChange={(e) => set({ he_con: e.target.value })}
                  placeholder="e.g. peanut allergy, asthma, diabetes…" />
                <TextField fullWidth required multiline rows={2} label="Medical History"
                  value={form.me_hi} onChange={(e) => set({ me_hi: e.target.value })}
                  placeholder="Surgeries, ongoing treatments, medications…" />
              </Box>
            )}

            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth required label="Emergency Contact Name"
                  value={form.e_na} onChange={(e) => set({ e_na: e.target.value })} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth required label="Emergency Contact Phone"
                  value={form.tele} onChange={(e) => set({ tele: e.target.value })} />
              </Grid>
            </Grid>
          </Paper>
        )}

        {/* STEP 5 — REVIEW */}
        {step === 5 && (
          <>
            <Paper sx={{ p: 3, mb: 2 }}>
              <SectionHeader icon={<ReviewIcon />} title="Review & Confirm"
                subtitle="Verify details before saving the record" />
              <Stack direction="row" spacing={3} alignItems="center">
                {form.image && (
                  <Box component="img" src={form.image} alt=""
                    sx={{ width: 100, height: 100, borderRadius: 2, objectFit: 'cover' }} />
                )}
                <Box>
                  <Typography variant="h5" fontWeight={800}>{form.firstname} {form.lastname}</Typography>
                  <Stack direction="row" spacing={1} mt={0.5}>
                    <Chip size="small" label={form.gender} variant="outlined" />
                    <Chip size="small" label={form.calss === 'Alumna' ? 'Alumni' : form.calss} color="primary" variant="outlined" />
                    <Chip size="small" label={form.type} color={form.type === 'Boarder' ? 'secondary' : 'default'} variant="outlined" />
                  </Stack>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontFamily: 'monospace' }}>
                    {form.index_no || '(auto-generated)'} · DOB {form.dob || '—'} · Registered {form.re_dat || '—'}
                  </Typography>
                </Box>
              </Stack>
            </Paper>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="overline" color="primary" fontWeight={700}>Father</Typography>
                  <Divider sx={{ my: 1 }} />
                  <ReviewRow label="Name"      value={form.parents.father.full_n} />
                  <ReviewRow label="Mobile"    value={form.parents.father.mo_num} />
                  <ReviewRow label="Email"     value={form.parents.father.e_add} />
                  <ReviewRow label="Occupation" value={form.parents.father.occu} />
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="overline" color="secondary" fontWeight={700}>Mother</Typography>
                  <Divider sx={{ my: 1 }} />
                  <ReviewRow label="Name"      value={form.parents.mother.full_n} />
                  <ReviewRow label="Mobile"    value={form.parents.mother.mo_num} />
                  <ReviewRow label="Email"     value={form.parents.mother.e_add} />
                  <ReviewRow label="Occupation" value={form.parents.mother.occu} />
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="overline" color="primary" fontWeight={700}>Guardian</Typography>
                  <Divider sx={{ my: 1 }} />
                  <ReviewRow label="Name"          value={form.p_name} />
                  <ReviewRow label="Relationship"  value={form.p_rele} />
                  <ReviewRow label="Mobile"        value={form.mobi} />
                  <ReviewRow label="Email"         value={form.p_email} />
                  <ReviewRow label="Address"       value={form.p_res} />
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="overline" color="primary" fontWeight={700}>Health</Typography>
                  <Divider sx={{ my: 1 }} />
                  <ReviewRow label="Allergies"          value={form.alle} />
                  <ReviewRow label="Health Conditions"  value={form.he_con} />
                  <ReviewRow label="Emergency Contact"  value={form.e_na} />
                  <ReviewRow label="Emergency Phone"    value={form.tele} />
                </Paper>
              </Grid>
            </Grid>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, bgcolor: 'background.default' }}>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Box sx={{ flex: 1 }} />
        <Button startIcon={<BackIcon />} onClick={handleBack} disabled={step === 0 || saving}>Back</Button>
        {step < STEPS.length - 1 ? (
          <Button variant="contained" endIcon={<NextIcon />} onClick={handleNext}>Next</Button>
        ) : (
          <Button variant="contained" color="primary"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={submit} disabled={saving}>
            {editing ? 'Save Changes' : 'Register Student'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

function ReviewRow({ label, value }) {
  return (
    <Stack direction="row" spacing={1} sx={{ py: 0.4 }}>
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 110, fontWeight: 700 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ flex: 1 }}>{value || '—'}</Typography>
    </Stack>
  );
}
